/**
 * @fileoverview Global Error Capture Hook
 * 
 * Catches unhandled JS errors and promise rejections that slip past
 * React error boundaries. Writes them to the events table via logEventDirect().
 * 
 * Features:
 * - window.onerror for uncaught JS errors
 * - unhandledrejection for failed promises
 * - Deduplication: same message within 5s is ignored
 * - Truncated stack traces (500 chars max)
 * - Chunk/module errors are preventDefault'd to avoid browser-level fatal behavior
 */

import { useEffect } from 'react';
import { logEventDirect } from './useEventLogger';
import { useAuth } from './useAuth';

const recentErrors = new Map<string, number>();
const DEDUP_WINDOW_MS = 5000;

const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'loading chunk',
  'loading css chunk',
  'dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
];

function isChunkErrorMessage(msg: string): boolean {
  const lower = msg.toLowerCase();
  return CHUNK_ERROR_PATTERNS.some(p => lower.includes(p));
}

function isDuplicate(key: string): boolean {
  const now = Date.now();
  const lastSeen = recentErrors.get(key);
  if (lastSeen && now - lastSeen < DEDUP_WINDOW_MS) {
    return true;
  }
  recentErrors.set(key, now);
  // Clean old entries
  if (recentErrors.size > 50) {
    for (const [k, v] of recentErrors) {
      if (now - v > DEDUP_WINDOW_MS) recentErrors.delete(k);
    }
  }
  return false;
}

/** Safely extract a message string from any rejection reason */
function extractReasonMessage(reason: unknown): string {
  if (reason instanceof Error) return reason.message;
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object' && 'message' in reason) {
    return String((reason as { message: unknown }).message);
  }
  try {
    return JSON.stringify(reason)?.substring(0, 300) ?? 'Unknown rejection';
  } catch {
    return `Unhandled rejection (${typeof reason})`;
  }
}

export function useGlobalErrorCapture() {
  const { user } = useAuth();

  useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const message = event.message || 'Unknown error';
      if (isDuplicate(message)) return;

      // Keep browser default error behavior for visibility during debugging/recovery.
      // We still log these events for backend diagnostics.

      logEventDirect('js_error', 'error', {
        error_type: 'uncaught_error',
        error_message: message.substring(0, 500),
        stack_trace: event.error?.stack?.substring(0, 500) || null,
        filename: event.filename || null,
        lineno: event.lineno || null,
        colno: event.colno || null,
      }, user?.id);
    };

    const handleRejection = (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      const message = extractReasonMessage(reason);

      if (isDuplicate(message)) return;

      // Keep default rejection behavior so runtime issues are visible in console/UI.

      logEventDirect('promise_rejection', 'error', {
        error_type: 'unhandled_rejection',
        error_message: message.substring(0, 500),
        stack_trace: reason instanceof Error ? reason.stack?.substring(0, 500) || null : null,
        reason_type: typeof reason,
      }, user?.id);
    };

    window.addEventListener('error', handleError);
    window.addEventListener('unhandledrejection', handleRejection);

    return () => {
      window.removeEventListener('error', handleError);
      window.removeEventListener('unhandledrejection', handleRejection);
    };
  }, [user?.id]);
}
